// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import EditAffiliateModal from '@/components/admin-dashboard/EditAffiliateModal';
import AffiliateFinanceTab from '@/components/admin-dashboard/AffiliateFinanceTab';
import AffiliatePromoMaterialsTab from '@/components/admin-dashboard/AffiliatePromoMaterialsTab';
import AdminReferralLinksTab from '@/components/admin-dashboard/AdminReferralLinksTab';
import AffiliateCouponCodesTab from '@/components/admin-dashboard/AffiliateCouponCodesTab';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AffiliateData } from '@/types/affiliate';

const AffiliateManagement = () => {
  const { toast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedAffiliate, setSelectedAffiliate] = useState<AffiliateData | undefined>(undefined);
  const [affiliates, setAffiliates] = useState<AffiliateData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAffiliates = async () => {
    try {
      setIsLoading(true);
      
      // Get calculated earnings from the database function
      const { data: payoutData, error: payoutError } = await supabase.rpc('get_affiliate_payouts');

      if (payoutError) {
        console.error('Error fetching affiliate payouts:', payoutError);
        toast({
          title: "Error",
          description: "Failed to load affiliates",
          variant: "destructive",
        });
        return;
      }

      // Get user_id and created_at from affiliates table
      const { data: affiliatesData, error: affiliatesError } = await supabase
        .from('affiliates')
        .select('id, user_id, created_at')
        .eq('status', 'active');

      if (affiliatesError) {
        console.error('Error fetching affiliates data:', affiliatesError);
        toast({
          title: "Error",
          description: "Failed to load affiliates",
          variant: "destructive",
        });
        return;
      }

      // Merge the data
      const transformedAffiliates = payoutData?.map((payout: any) => {
        const affiliateData = affiliatesData?.find(a => a.id === payout.id);
        return {
          id: payout.id,
          user_id: affiliateData?.user_id || '',
          name: payout.name,
          email: payout.email,
          status: payout.status,
          commission_rate: parseFloat(payout.commission_rate),
          referral_code: payout.referral_code,
          connections: parseInt(payout.connections) || 0,
          active_connections: parseInt(payout.active_connections) || 0,
          conversions: parseInt(payout.conversions) || 0,
          total_earnings: parseFloat(payout.earnings), // Using calculated earnings from conversions
          created_at: affiliateData?.created_at || new Date().toISOString()
        };
      }) || [];

      console.log('✅ Affiliates loaded:', transformedAffiliates.length);
      console.log('Affiliate data sample:', transformedAffiliates[0]);

      setAffiliates(transformedAffiliates);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      toast({
        title: "Error",
        description: "Failed to load affiliates",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const handleNewAffiliate = () => {
    setModalMode('create');
    setSelectedAffiliate(undefined);
    setIsModalOpen(true);
  };

  const handleEditAffiliate = (affiliate: AffiliateData) => {
    setModalMode('edit');
    setSelectedAffiliate(affiliate);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAffiliate(undefined);
  };

  const handleSaveAffiliate = (data: AffiliateData) => {
    console.log('Saving affiliate:', data);
    // Refresh the affiliates list after successful creation/update
    fetchAffiliates();
    
    if (modalMode === 'create') {
      toast({
        title: "Success",
        description: "Affiliate created successfully",
      });
    } else {
      toast({
        title: "Success",
        description: "Affiliate updated successfully",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-primary-700">Affiliate Management</h1>
          <p className="text-gray-600">
            Manage affiliate partners and their commission settings
          </p>
        </div>
        <Button onClick={handleNewAffiliate} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          New Affiliate
        </Button>
      </div>

      <Tabs defaultValue="affiliates" className="space-y-6">
        <TabsList>
          <TabsTrigger value="affiliates">Affiliates</TabsTrigger>
          <TabsTrigger value="referral-links">Referral Links</TabsTrigger>
          <TabsTrigger value="coupon-codes">Coupon Codes</TabsTrigger>
          <TabsTrigger value="finance">Finance</TabsTrigger>
          <TabsTrigger value="promo-materials">Promo Materials</TabsTrigger>
        </TabsList>

        <TabsContent value="affiliates">
          <Card>
            <CardHeader>
              <CardTitle>Affiliates</CardTitle>
              <CardDescription>
                Overview of all affiliate partners and their performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">Loading affiliates...</p>
                </div>
              ) : affiliates.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg mb-4">No affiliates found</p>
                  <p className="text-gray-400 mb-6">Get started by adding your first affiliate partner</p>
                  <Button onClick={handleNewAffiliate} className="flex items-center gap-2 mx-auto">
                    <Plus className="h-4 w-4" />
                    Add First Affiliate
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Name</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Email</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Commission Rate (%)</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Referral Code</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-700">Athletes Connected</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-700">Athletes Active</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-700">Athletes Converted</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Total Earnings</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliates.map((affiliate, index) => (
                        <tr 
                          key={affiliate.id} 
                          className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }`}
                        >
                          <td className="py-3 px-4 font-medium text-gray-900">
                            {affiliate.name}
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {affiliate.email}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              affiliate.status === 'active' ? 'bg-green-100 text-green-800' :
                              affiliate.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              affiliate.status === 'suspended' ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {affiliate.status.charAt(0).toUpperCase() + affiliate.status.slice(1)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {affiliate.commission_rate}%
                          </td>
                          <td className="py-3 px-4 text-gray-600 font-mono text-sm">
                            {affiliate.referral_code}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={
                              (affiliate.connections || 0) > 0 && (affiliate.conversions || 0) === 0 
                                ? 'text-yellow-600 font-medium' 
                                : 'text-gray-600'
                            }>
                              {affiliate.connections || 0}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-blue-600 font-medium">
                              {affiliate.active_connections || 0}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-gray-600">
                            {affiliate.conversions || 0}
                          </td>
                          <td className="py-3 px-4 text-gray-600 font-medium">
                            ${affiliate.total_earnings.toFixed(2)}
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditAffiliate(affiliate)}
                              className="text-xs"
                            >
                              Edit
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referral-links">
          <AdminReferralLinksTab />
        </TabsContent>

        <TabsContent value="coupon-codes">
          <AffiliateCouponCodesTab />
        </TabsContent>

        <TabsContent value="finance">
          <AffiliateFinanceTab />
        </TabsContent>

        <TabsContent value="promo-materials">
          <AffiliatePromoMaterialsTab />
        </TabsContent>
      </Tabs>

      <EditAffiliateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
        initialData={selectedAffiliate}
        onSave={handleSaveAffiliate}
      />
    </div>
  );
};

export default AffiliateManagement;
